import React, {
  useRef,
  Children,
  forwardRef,
  useContext,
  createContext,
  cloneElement,
  useCallback,
  useEffect,
} from 'react';

import { arrayOf, element, func, node, shape, number, bool } from 'prop-types';

import { FixedSizeList, VariableSizeList } from 'react-window';

import useWindowResize from 'hooks/useWindowResize';

const LISTBOX_PADDING = 8;
const ITEM_SIZE = 40;

const renderFixSizedRow = props => {
  const { data, index, style } = props;

  return cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
};

// https://github.com/bvaughn/react-window/issues/582
// https://github.com/bvaughn/react-window/issues/457
// https://codesandbox.io/s/react-window-dynamic-row-height-8ftbq
const VariableSizeRow = ({ data, index, setSize, windowWidth }) => {
  const rowRef = useRef();

  useEffect(() => {
    const currentHeight = rowRef.current?.getBoundingClientRect().height;
    const size = currentHeight < ITEM_SIZE ? ITEM_SIZE : currentHeight;
    setSize(index, size);
  }, [setSize, index, windowWidth]);

  return <div ref={rowRef}>{data[index]}</div>;
};

VariableSizeRow.propTypes = {
  data: shape({}).isRequired,
  index: number.isRequired,
  setSize: func.isRequired,
  windowWidth: number.isRequired,
};

const OuterElementContext = createContext({});

const OuterElementType = forwardRef((props, ref) => {
  const outerProps = useContext(OuterElementContext);

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <div ref={ref} {...props} {...outerProps} />;
});

// Adapter for react-window
const ListBoxAdapterComponent = forwardRef((props, ref) => {
  const { children, onScroll, menuEl, useVariableSizeList, ...other } = props;

  const itemData = Children.toArray(children);
  const count = itemData.length;

  const listRef = useRef(null);
  const containerRef = useRef(null);
  const sizeMap = useRef({});
  const setSize = useCallback(
    (index, size) => {
      sizeMap.current = { ...sizeMap.current, [index]: size };
      listRef.current.resetAfterIndex(index);
    },
    [sizeMap, listRef],
  );
  const getSize = index => sizeMap.current[index] || ITEM_SIZE;
  const [windowWidth] = useWindowResize();

  const sharedListProps = () => ({
    itemData,
    height: itemData.reduce(a => a + ITEM_SIZE, 0),
    ref: listRef,
    outerElementType: OuterElementType,
    outerRef: containerRef,
    itemCount: count,
    onScroll: scrollData => onScroll(scrollData, containerRef),
    role: 'listbox',
  });

  return (
    <div ref={ref}>
      {menuEl}

      <OuterElementContext.Provider value={other}>
        {useVariableSizeList ? (
          <VariableSizeList itemSize={getSize} {...sharedListProps()}>
            {({ data, index, style }) => (
              <div style={style}>
                <VariableSizeRow
                  data={data}
                  index={index}
                  setSize={setSize}
                  windowWidth={windowWidth}
                />
              </div>
            )}
          </VariableSizeList>
        ) : (
          <FixedSizeList itemSize={ITEM_SIZE} {...sharedListProps()}>
            {renderFixSizedRow}
          </FixedSizeList>
        )}
      </OuterElementContext.Provider>
    </div>
  );
});

ListBoxAdapterComponent.propTypes = {
  children: arrayOf(element).isRequired,
  onScroll: func.isRequired,
  menuEl: node.isRequired,
  useVariableSizeList: bool.isRequired,
};

export default ListBoxAdapterComponent;
